/**
 * The device manifest: the contract between the API, the admin-panel preview,
 * and the Kotlin TV app.
 *
 * The guiding principle is that the DEVICE decides what plays right now, not the
 * server. The manifest therefore carries everything needed to make that decision
 * offline — timezone, opening hours, the whole schedule, every referenced
 * playlist and asset, and the active override — so a screen cut off from the
 * internet for a week still flips from lunch to dinner on time and still drops a
 * promo the day it expires.
 *
 * The Kotlin app mirrors these shapes by hand; `docs/manifest-contract.md` holds
 * the fixtures both sides test against.
 */

import { z } from 'zod';
import {
  HEARTBEAT_JITTER_SEC,
  HEARTBEAT_SEC,
  PLAYBACK_FLUSH_SEC,
} from './constants.js';
import { isHhMm, isValidTimeZone } from './time.js';

export const isoDayOfWeekSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
]);

export const hhMmSchema = z
  .string()
  .refine(isHhMm, { message: 'Expected a 24-hour time of day, e.g. "16:30"' });

/**
 * How far the app rotates its own rendering. Fire TV always outputs landscape
 * over HDMI and ignores android:screenOrientation, so a physically rotated TV is
 * served by rotating inside the landscape framebuffer.
 *
 *   0   landscape (default)
 *   90  portrait, TV turned clockwise
 *   180 landscape, upside down (awkward ceiling mounts)
 *   270 portrait, TV turned anticlockwise
 */
export const rotationDegreesSchema = z.union([
  z.literal(0),
  z.literal(90),
  z.literal(180),
  z.literal(270),
]);

export const fitModeSchema = z.enum(['contain', 'cover', 'stretch']);

export const assetTypeSchema = z.enum([
  'video/mp4',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/, 'Expected a lowercase hex SHA-256');
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a hex colour, e.g. "#000000"');

export const manifestAssetSchema = z.object({
  id: z.string().min(1),
  type: assetTypeSchema,
  url: z.string().url(),
  sha256: sha256Schema,
  bytes: z.number().int().positive(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Videos only; null for stills. */
  durationMs: z.number().int().positive().nullable().default(null),
  /** After this instant the asset stops playing, with or without a server round trip. */
  contentExpiresAt: z.string().datetime().nullable().default(null),
});

export const playlistItemSchema = z.object({
  assetId: z.string().min(1),
  /** Stills: seconds on screen. Ignored for video, which plays its natural length. */
  durationSec: z.number().positive().max(3600).nullable().default(null),
  /** Video: times to play through before advancing. Ignored for stills. */
  repeatCount: z.number().int().positive().max(100).nullable().default(null),
  muted: z.boolean().default(true),
  fit: fitModeSchema.default('contain'),
  backgroundColor: hexColorSchema.default('#000000'),
});

export const manifestPlaylistSchema = z.object({
  id: z.string().min(1),
  name: z.string().default(''),
  items: z.array(playlistItemSchema).default([]),
});

export const scheduleRuleSchema = z.object({
  id: z.string().min(1),
  /** Higher wins when two rules overlap; ties break toward the earlier rule. */
  priority: z.number().int().default(10),
  days: z.array(isoDayOfWeekSchema).min(1),
  /** Inclusive. */
  start: hhMmSchema,
  /** Exclusive. An end at or before the start wraps past midnight. */
  end: hhMmSchema,
  playlistId: z.string().min(1),
});

export const openingHoursEntrySchema = z.object({
  day: isoDayOfWeekSchema,
  open: hhMmSchema,
  /** A close at or before the open wraps past midnight — Friday 11:00 to 02:30. */
  close: hhMmSchema,
});

export const manifestOverrideSchema = z
  .object({
    /** Which scope won: a screen override beats a store one, which beats fleet-wide. */
    source: z.enum(['all', 'store', 'screen']),
    playlistId: z.string().min(1).nullable().default(null),
    assetId: z.string().min(1).nullable().default(null),
    startsAt: z.string().datetime().nullable().default(null),
    endsAt: z.string().datetime(),
  })
  .refine(
    (value) => (value.playlistId === null) !== (value.assetId === null),
    { message: 'An override names either a playlist or a single asset, not both' },
  );

export const appUpdateSchema = z.object({
  latestVersionCode: z.number().int().positive(),
  versionName: z.string().default(''),
  apkUrl: z.string().url(),
  sha256: sha256Schema,
  /** Skip the wait for a quiet moment and prompt for the install immediately. */
  force: z.boolean().default(false),
});

export const pollingSchema = z.object({
  heartbeatSec: z.number().int().positive().default(HEARTBEAT_SEC),
  jitterSec: z.number().int().nonnegative().default(HEARTBEAT_JITTER_SEC),
  playbackFlushSec: z.number().int().positive().default(PLAYBACK_FLUSH_SEC),
});

export const manifestScreenSchema = z.object({
  id: z.string().min(1),
  name: z.string().default(''),
  rotationDegrees: rotationDegreesSchema.default(0),
  /**
   * Whether the device may bring its player to the foreground on its own — after a
   * power cut, after a self-update, after the platform kills and restarts its
   * process, and after somebody presses HOME on the remote.
   *
   * This is NOT a kill switch. `false` never stops the sync service, the heartbeat,
   * manifest sync, downloads or the command channel. A screen with auto-start off
   * is a dark television that is still fully reachable, which is the only version
   * of this setting that is not a one-way door: the device that has been told to
   * stay dark is the device that must still be able to hear "come back".
   *
   * A person with a remote can always launch the app from the Fire TV launcher row
   * whatever this says. That is deliberate — it is the on-site recovery path.
   *
   * Absent means `true`, on every side. See `docs/manifest-contract.md` §2.2.
   */
  autoStart: z.boolean().default(true),
});

export const deviceManifestSchema = z.object({
  /** "sha256:<hex>" over the canonical encoding of everything else. Also the HTTP ETag. */
  manifestVersion: z.string().min(1),
  generatedAt: z.string().datetime(),
  screen: manifestScreenSchema,
  /** IANA zone of the owning store, e.g. "Pacific/Auckland". */
  timezone: z.string().refine(isValidTimeZone, { message: 'Unknown IANA timezone' }),
  /** Empty means the screen runs around the clock. */
  openingHours: z.array(openingHoursEntrySchema).default([]),
  schedule: z.array(scheduleRuleSchema).default([]),
  defaultPlaylistId: z.string().min(1).nullable().default(null),
  fallbackAssetId: z.string().min(1).nullable().default(null),
  override: manifestOverrideSchema.nullable().default(null),
  playlists: z.record(z.string(), manifestPlaylistSchema).default({}),
  assets: z.record(z.string(), manifestAssetSchema).default({}),
  app: appUpdateSchema.nullable().default(null),
  polling: pollingSchema,
  /** Total bytes of every asset referenced here — the device checks it against free space. */
  referencedBytes: z.number().int().nonnegative().default(0),
});

export type IsoDayOfWeekValue = z.infer<typeof isoDayOfWeekSchema>;
export type RotationDegrees = z.infer<typeof rotationDegreesSchema>;
export type FitMode = z.infer<typeof fitModeSchema>;
export type AssetType = z.infer<typeof assetTypeSchema>;
export type ManifestAsset = z.infer<typeof manifestAssetSchema>;
export type PlaylistItem = z.infer<typeof playlistItemSchema>;
export type ManifestPlaylist = z.infer<typeof manifestPlaylistSchema>;
export type ScheduleRule = z.infer<typeof scheduleRuleSchema>;
export type OpeningHoursEntry = z.infer<typeof openingHoursEntrySchema>;
export type ManifestOverride = z.infer<typeof manifestOverrideSchema>;
export type AppUpdate = z.infer<typeof appUpdateSchema>;
export type PollingConfig = z.infer<typeof pollingSchema>;
export type ManifestScreen = z.infer<typeof manifestScreenSchema>;
export type DeviceManifest = z.infer<typeof deviceManifestSchema>;

export function isVideoAsset(asset: ManifestAsset): boolean {
  return asset.type === 'video/mp4';
}
